/**
 * English dictionary — the structural source of truth. `km.ts` is typed
 * against this object, so a missing Khmer string is a compile error rather
 * than an English word appearing in a Khmer storefront.
 *
 * CLAUDE.md §6.2: user-facing text ships in English or Khmer only, Khmer is
 * the customer default, and nothing may be hard-coded in a component.
 *
 * Placeholders use {{name}} and are substituted by `t()`.
 */

export const en = {
  common: {
    today: 'Today',
    add: 'Add',
    until: 'Until',
    appName: 'TeleStore',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    back: 'Back',
    next: 'Next',
    done: 'Done',
    close: 'Close',
    retry: 'Try again',
    loading: 'Loading…',
    search: 'Search',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    optional: 'optional',
    required: 'required',
    copy: 'Copy',
    remove: 'Remove',
    copied: 'Copied',
    share: 'Share',
    from: 'From',
    total: 'Total',
    subtotal: 'Subtotal',
    shipping: 'Shipping',
    free: 'Free',
    quantity: 'Quantity',
    price: 'Price',
    size: 'Size',
    color: 'Color',
    outOfStock: 'Out of stock',
    lowStock: 'Only {{count}} left',
    inStock: 'In stock',
    empty: 'Nothing here yet',
    sun: 'Sun',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
  },

  storefront: {
    shopClosed: 'This shop is not taking orders right now',
    chatWithShop: 'Chat with the shop',
    viewCart: 'View cart',
    addToCart: 'Add to cart',
    buyNow: 'Buy now',
    cartEmpty: 'Your cart is empty',
    continueShopping: 'Continue shopping',
    checkout: 'Checkout',
    categories: 'Categories',
    searchProducts: 'Search products',
    noProducts: 'No products found',
    selectSize: 'Choose a size',
    selectColor: 'Choose a colour',
    selectOptions: 'Choose your options',
    productUnavailable: 'This item is no longer available',
    referencePrice: '≈ {{amount}}',
    openInTelegram: 'Open in Telegram',

    /* §4 F&B */
    modifierRequired: 'Required',
    modifierOptional: 'Optional',
    modifierUpTo: 'Up to {{count}}',
    chooseGroup: 'Choose {{group}}',
    notSellingNow: 'Not available right now',
    prepTime: 'Ready in about {{count}} min',
    pickup: 'Pick up',
    localDelivery: 'Delivery',
    dineIn: 'Dine in',
    chooseFulfilment: 'How would you like it?',

    /* §4 services */
    chooseStaff: 'Choose who',
    anyStaff: 'Anyone',
    chooseDate: 'Choose a day',
    chooseTime: 'Choose a time',
    bookNow: 'Book',
    slotsClosed: 'Closed on this day',
    slotsFull: 'Fully booked — try another day',
    slotsPast: 'This day has passed',
    depositNotice: 'A deposit holds your slot. Your time is confirmed once it arrives.',
    appointment: 'Appointment',
  },

  checkout: {
    title: 'Checkout',
    yourDetails: 'Your details',
    name: 'Full name',
    phone: 'Phone number',
    deliveryAddress: 'Delivery address',
    province: 'Province',
    district: 'District',
    addressLine: 'Street, house number, landmark',
    note: 'Note for the shop',
    paymentMethod: 'Payment method',
    payByTransfer: 'Bank transfer (KHQR)',
    payByTransferHint: 'Scan with any bank app. We confirm your order once the transfer lands.',
    payOnDelivery: 'Cash on delivery',
    payOnDeliveryHint: 'Pay the courier when your order arrives.',
    placeOrder: 'Place order',
    placing: 'Placing your order…',
    shippingUnavailable: 'The shop does not deliver to this province yet',
    selectProvinceFirst: 'Choose a province to see the delivery fee',
    phoneInvalid: 'Enter a Cambodian phone number, e.g. 012 345 678',
    nameRequired: 'Please enter your name',
    addressRequired: 'Please enter a delivery address',
    promoCode: 'Promo code',
    promoPlaceholder: 'Enter code',
    applyCode: 'Apply',
    promoNotApplied: 'Not applied: {{codes}}',
  },

  payment: {
    scanToPay: 'Scan to pay',
    transferExactly: 'Transfer exactly {{amount}}',
    exactAmountHint:
      'The amount must match exactly — that is how the shop matches your transfer to this order.',
    payTo: 'Pay to',
    expiresIn: 'This code expires in {{minutes}} minutes',
    expired: 'This payment code has expired',
    requestNewCode: 'Get a new code',
    awaitingPayment: 'Waiting for your transfer',
    paymentReceived: 'Payment received',
    iHavePaid: 'I have transferred',
    iHavePaidHint: 'The shop will confirm shortly.',
    saveQr: 'Save QR image',
    codAmountDue: 'Pay {{amount}} to the courier',
  },

  order: {
    title: 'Order {{code}}',
    yourOrders: 'Your orders',
    noOrders: 'You have not ordered from this shop yet',
    placedAt: 'Placed {{date}}',
    items: 'Items',
    trackOrder: 'Track order',
    getUpdatesOnTelegram: 'Get order updates on Telegram',
    getUpdatesHint: 'One tap. We will message you when your order ships.',
    orderPlaced: 'Order placed',
    orderPlacedHint: 'The shop will confirm your order shortly.',
    cancelOrder: 'Cancel order',
    reorder: 'Order again',
    exchangeOf: 'Exchange for order {{code}}',
    status: {
      draft: 'Draft',
      pending_confirm: 'Awaiting shop confirmation',
      awaiting_payment: 'Awaiting payment',
      paid: 'Paid',
      fulfilling: 'Being prepared',
      confirmed: 'Confirmed',
      shipped: 'Shipped',
      collected: 'Cash collected',
      remitted: 'Payment remitted',
      scheduled: 'Booked',
      no_show: 'Did not attend',
      completed: 'Completed',
      cancelled: 'Cancelled',
      refunded: 'Refunded',
    },
  },

  bot: {
    createForMe: 'Create my bot for me',
    createForMeHint: 'Telegram will ask you to confirm the name. You never copy a token.',
    openInTelegram: 'Open in Telegram',
    orPasteToken: 'or paste a token',
    welcome: 'Welcome to {{shop}} 👋',
    welcomeHint: 'Browse the shop, check an order, or ask us a question.',
    openShop: 'Open shop',
    myOrders: 'My orders',
    help: 'Help',
    contactShop: 'Talk to the shop',
    unknownCommand: 'I did not understand that. Use the buttons below.',
    orderStatusPrompt: 'Which order would you like to check?',
    noOrdersFound: 'I could not find any orders for you in this shop.',
    shippingFeeTitle: 'Delivery fees',
    shippingFeeLine: '{{province}} — {{fee}}',
    shippingFeeNone: 'The shop has not published delivery fees yet.',
    openingHoursTitle: 'Opening hours',
    openNow: 'Open now',
    closedNow: 'Closed now',
    returnPolicyTitle: 'Returns',
    noReturnPolicy: 'The shop has not published a returns policy yet.',
    faqTitle: 'Common questions',
    escalated: 'I have passed your question to the shop. They will reply here shortly.',
    privacyBlocked: 'You can only look up your own orders.',
  },

  merchant: {
    newOrder: 'New order {{code}}',
    confirmOrder: 'Confirm',
    rejectOrder: 'Reject',
    markPaid: 'Mark as paid',
    markShipped: 'Mark as shipped',
    markCollected: 'Cash collected',
    markRemitted: 'Courier remitted',
    markCompleted: 'Complete',
    viewOrder: 'View order',
    todaySummary: 'Today: {{orders}} orders · {{revenue}}',
    lowStockTitle: 'Low stock',
    lowStockLine: '{{product}} — {{count}} left',
    paymentMatched: 'Payment matched to order {{code}}',
    paymentUnmatched: 'A transfer of {{amount}} arrived but matches no open order',
    paymentUnmatchedHint: 'Tap an order below to attach it, or ignore.',
    listenerBroken: 'I can no longer read your ABA notification group',
    listenerBrokenHint: 'Make sure the bot is still an admin in the group.',
  },

  notification: {
    'order.placed':
      'Thank you! Order {{code}} is placed. Total {{total}}. The shop will confirm shortly.',
    'order.awaiting_payment':
      'Order {{code}} is confirmed. Please transfer exactly {{amount}} using the QR code above.',
    'order.payment_reminder':
      'Reminder: order {{code}} is still waiting for your transfer of {{amount}}. The code expires in {{minutes}} minutes.',
    'order.paid': 'We received your payment for order {{code}}. Thank you!',
    'order.confirmed': 'Order {{code}} is confirmed. You will pay {{amount}} on delivery.',
    'order.shipped': 'Order {{code}} has shipped. {{tracking}}',
    'order.completed': 'Order {{code}} is complete. Thank you for shopping with {{shop}}!',
    'order.cancelled': 'Order {{code}} was cancelled. {{reason}}',
    'order.refunded': 'A refund of {{amount}} for order {{code}} has been sent.',
    'order.exchange_created': 'Exchange order {{code}} created for your original order {{original}}.',
    'merchant.new_order': 'New order {{code}} — {{total}} ({{payment}})',
    'merchant.payment_received': 'Payment of {{amount}} received for order {{code}}',
    'merchant.payment_unmatched': 'Unmatched transfer: {{amount}} from {{payer}}',
    'merchant.low_stock': 'Low stock: {{product}} ({{count}} left)',
    'merchant.cod_collected': 'Courier collected {{amount}} for order {{code}}',
    'merchant.staff_joined': '{{name}} joined your shop as {{role}}',
    'merchant.subscription_reminder':
      'Your {{plan}} plan renews in {{days}} days. Tap to pay {{amount}}.',
    'merchant.subscription_expired':
      'Your subscription has lapsed. Your storefront stays online, but premium features are locked until you renew.',
    'merchant.credits_low': 'You have used 80% of this month’s credits ({{remaining}} left).',
    'merchant.credits_exhausted': 'You are out of credits. Tap to top up.',
    'merchant.ai_job_done': 'Your {{job}} is ready.',
    'merchant.provider_degraded':
      '{{provider}} is not responding. Checkout has switched to KHQR transfer so orders keep flowing.',
    'merchant.aba_listener_broken':
      'I can no longer read your ABA notification group — payments will need manual confirmation until it is fixed.',
    'customer.win_back': 'We miss you at {{shop}}! New arrivals are in — take a look.',
    'ops.exchange_rate_stale':
      'The USD/KHR reference rate has not updated for {{days}} days. Still showing {{rate}} from {{effectiveOn}} — check the NBC scraper.',
    'booking.reminder_24h':
      'Reminder: your appointment at {{shop}} is tomorrow, {{when}}. {{service}} with {{who}}.',
    'booking.reminder_2h':
      'Your appointment at {{shop}} is at {{when}} — about 2 hours away. {{service}} with {{who}}.',
    'booking.rescheduled': 'Your appointment at {{shop}} has moved to {{when}}.',
    'ops.new_lead': 'New lead: {{name}} · {{contact}} · sells {{sells}} · via {{source}}',
  },

  admin: {
    planFree: 'Free',
    shopName: 'Shop name',
    integrationsTitle: 'Integrates with',
    createShopHeading: 'Create your shop',
    shopNamePlaceholder: 'e.g. Sokha Boutique',
    createShopFooter: 'Add products and connect your bot after this — nothing here is final except what you sell.',
    listingCurrency: 'Price your products in',
    listingCurrencyHint:
      'The other currency is always shown beside the total, at the National Bank of Cambodia’s official rate — updated daily, never typed by you. You can change this later.',
    verticalFixedHint: 'This cannot be changed later.',
    whatDoYouSell: 'What do you sell?',
    verticalRetail: 'Physical goods you ship (clothes, cosmetics, homeware)',
    verticalFnb: 'Food and drink',
    verticalServices: 'Appointments (salon, clinic, studio)',
    verticalDigital: 'Codes and vouchers (game top-up, gift cards, ebooks)',
    codes: 'Codes',
    codesInStock: 'in stock',
    codesUndelivered: 'paid, not yet delivered',
    codesSold: 'sold',
    codesLow: 'Running out',
    codesPaste: 'Paste codes, one per line',
    codesAdd: 'Add to stock',
    codesAdded: 'Added {{count}} codes',
    codesInstructions: 'Redemption instructions',
    codesInstructionsHint: 'Sent with every code. Where to redeem it, and by when.',
    codesLowThreshold: 'Warn me when fewer than',
    codesEmpty: 'No products yet. Add one, then paste its codes here.',
    dashboard: 'Dashboard',
    orders: 'Orders',
    products: 'Products',
    customers: 'Customers',
    settings: 'Settings',
    team: 'Team',
    teamJoined: 'You have joined {{shop}} as {{role}}. Open the dashboard to start.',
    billing: 'Billing',
    theme: 'Theme',
    delivery: 'Delivery',
    payments: 'Payments',
    support: 'Support',
    signIn: 'Sign in with Telegram',
    signOut: 'Sign out',
    createShopIntro:
      'Free to start, no card. Take KHQR payments any bank app can scan — ABA included — and let an AI assistant answer your customers.',
    createShop: 'Create shop',
    revenue: 'Revenue',
    ordersToday: 'Orders today',
    pendingConfirm: 'Awaiting confirmation',
    awaitingPayment: 'Awaiting payment',
    readyToShip: 'Ready to ship',
    addProduct: 'Add product',
    importFromFacebook: 'Import from Facebook',
    replyKit: 'Copy reply for Facebook',
    variants: 'Variants',
    addVariant: 'Add variant',
    stock: 'Stock',
    publish: 'Publish',
    unpublish: 'Unpublish',
    shops: 'Shops',
    creditsShared: 'Your plan and credits cover every shop you own.',
    credits: 'Credits',
    creditsRemaining: '{{count}} credits left',
    topUp: 'Top up',
    plan: 'Plan',
    changePlan: 'Change plan',
    upgradeToUnlock: 'Upgrade to unlock',
    inviteStaff: 'Invite staff',
    inviteLink: 'Share this link with your staff member',
    inviteInvalid: 'Ask the shop owner to send you a new invite link.',
    staffCapHint: 'Your plan includes {{count}} team member. Upgrade to add more.',
    upgrade: 'Upgrade',
    planActivated: 'Payment received — your {{plan}} plan is active.',
    creditsAdded: 'Payment received — {{credits}} credits added to your balance.',
    payToUpgrade: 'Scan to pay {{amount}}',
    payToUpgradeHint:
      'Transfer this EXACT amount — the last digits are how we recognise your payment. Your plan activates within minutes.',
    chargePending: 'Waiting for your transfer',
    removeMember: 'Remove',
    khqrSetup: 'KHQR setup',
    khqrSetupHint: 'Enter the ABA account id shown in your bank app. Customers can pay this KHQR from any bank app.',
    abaAccountId: 'Bank identifier',
    abaAccountIdHint: 'Already filled in for ABA. Change it only if you bank elsewhere.',
    abaAccountNumber: 'Your ABA account number',
    abaAccountNumberHint: 'The digits on your ABA QR card. This is where the money arrives.',
    khqrUpload: 'Upload your ABA/KHQR here',
    khqrUploadHint:
      'Choose a photo of your payment QR card. We read your details from it — the image never leaves this device.',
    khqrScanning: 'Reading the code…',
    khqrManualInstead: 'Enter the details by hand instead',
    khqrConfirmTitle: 'Money will arrive in this account',
    khqrConfirmSave: 'That is correct — save',
    khqrRetake: 'Take another photo',
    khqrNotAba:
      'You can start selling right away. Automatic payment matching only works with ABA for now, so mark transfers as received yourself.',
    khqrErrNoCode: 'No QR code found. Move closer, find better light, and keep the code in the middle of the frame.',
    khqrErrNotKhqr: 'That image has a QR code, but it is not a payment code.',
    khqrErrChecksum: 'The code could not be read cleanly. Please take the photo again.',
    khqrErrNoAccount:
      'This code carries no account number, so money paid to it would never arrive. Use the receiving QR in your bank app, or enter the details by hand.',
    khqrErrNoLatinName:
      'This code only carries a Khmer name. Enter a Latin name by hand — it is what shows in the bank app your customer pays from.',
    abaGroupSetup: 'ABA notification group',
    abaGroupHint:
      'Add this bot to your ABA notification group and make it an admin. We only read; we never post there.',
    exportData: 'Export all data (CSV)',
    deleteBot: 'Delete my bot',
  },

  booking: {
    chooseTime: 'Choose a time',
    chooseWith: 'Who with?',
    anyAvailable: 'Anyone available',
    noSlots: 'Nothing free that day',
    closedThatDay: 'Closed that day',
    fullyBooked: 'Fully booked that day',
    pickAnotherDay: 'Try another day',
    yourAppointment: 'Your appointment',
    reschedule: 'Change time',
    cancelBooking: 'Cancel',
    rescheduled: 'Your appointment has been moved',
    slotTaken: 'Somebody just took that time. Please pick another.',
    reminderTomorrow: 'Reminder: you have an appointment at {{shop}} tomorrow.',
    reminderSoon: 'Your appointment at {{shop}} is in about 2 hours.',
    depositHolds: 'Transfer the deposit within {{minutes}} minutes to keep this time.',
    bookedOnTrust: 'No deposit needed — just come at your time.',
    duration: '{{minutes}} min',
  },

  fnb: {
    menu: 'Menu',
    sellingHoursAllDay: 'All day',
    soldOutBoard: 'Sold out today',
    soldOutHint: 'Tap an item to take it off the menu. Tap again to put it back.',
    modifierGroupsHint: 'A group of choices — sugar level, size, add-ons. Write it once and attach it to as many items as you like.',
    addOption: 'Add option',
    isAvailable: 'Available',
    chooseOptions: 'Choose your options',
    required: 'Required',
    pickUpTo: 'Pick up to {{count}}',
    soldOut: 'Sold out',
    notServedNow: 'Not being served right now',
    pickup: 'Pickup',
    localDelivery: 'Delivery',
    dineIn: 'Dine in',
    howToGetIt: 'How would you like it?',
    whenToCollect: 'When will you collect it?',
    asSoonAsPossible: 'As soon as possible',
    readyIn: 'Ready in about {{minutes}} min',
    deliveryMinimum: 'Minimum {{amount}} for delivery',
    kitchenTicket: 'Kitchen ticket',
    markSoldOut: 'Sold out',
    markAvailable: 'Back on',
    restockAll: 'Everything back on',
    modifierGroups: 'Option groups',
    newGroup: 'New option group',
    groupName: 'Group name',
    selectionType: 'How many can they pick?',
    pickOne: 'Exactly one',
    pickMany: 'Several',
    minSelect: 'At least',
    maxSelect: 'At most',
    optionName: 'Option',
    priceDelta: 'Extra charge',
    isDefault: 'Default',
    prepMinutes: 'Preparation time (minutes)',
    sellingHours: 'Only served at certain hours',
    attachedItems: 'Used by {{count}} items',
  },

  resources: {
    staffHint: 'Whoever can be booked. Their working hours decide which times customers see.',
    blockHint: 'A lunch break or a day off. Customers cannot book over it.',
    dayOff: 'Day off',
    inactive: 'Inactive',
    deactivate: 'Deactivate',
    daysPerWeek: '{{count}} days a week',
    noHours: 'No hours set',
    title: 'Who takes bookings',
    newResource: 'Add person',
    resourceName: 'Name',
    linkedStaff: 'Staff member',
    notLinked: 'Not a staff member',
    workingHours: 'Working hours',
    addWindow: 'Add hours',
    calendar: 'Calendar',
    blockTime: 'Block time off',
    blocked: 'Blocked',
    noAppointments: 'Nothing booked in this period',
    serviceSettings: 'Booking settings',
    durationMinutes: 'How long it takes (minutes)',
    bufferMinutes: 'Gap afterwards (minutes)',
    depositAmount: 'Deposit to hold the slot',
    noDeposit: 'No deposit',
    whoCanDoIt: 'Who can do this',
    anyone: 'Anyone',
  },

  trust: {
    shopSuspended: 'This shop is not taking orders',
    shopSuspendedHint:
      'TeleStore has paused this shop while we look into a report. Any order you already placed is unaffected.',
    reportShop: 'Report this shop',
    reportHint:
      'Tell us if this shop sells something it should not, or took your money and never delivered. You do not need an account.',
    reportReason: 'What is wrong?',
    reportDetail: 'What happened? (optional)',
    reportContact: 'How can we reach you? (optional)',
    reportSubmit: 'Send report',
    reportThanks: 'Thank you. A person will read this.',

    policy: 'Policies',
    prohibitedGoods: 'What you may not sell',
    prohibitedIntro:
      'Shops on TeleStore may not sell these. If you are unsure whether something counts, ask us before listing it.',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    accept: 'I have read and accept',
    accepted: 'Accepted {{date}}',
    acceptAll: 'Accept and continue',
    outstanding: 'Please read and accept these',
    outstandingHint:
      'Every shop accepts these before going live. It takes a minute and you only do it once.',
    updated: 'This has changed since you accepted it',
    returnTemplate: 'Returns policy',
    returnTemplateHint:
      'Pick one to start from. It is copied into your settings, and you can edit it afterwards.',
    adoptTemplate: 'Use this one',
    currentReturnPolicy: 'Your current returns policy',
    reportsAboutYou: 'Reports about your shop',
    noReports: 'Nobody has reported your shop.',
    moderationHistory: 'Decisions',
    suspendedBanner: 'Your shop is suspended',
    suspendedBannerHint:
      'Your storefront has stopped taking orders. Your data and existing orders are untouched — you can still export everything. Message us on Telegram if you think this is wrong.',
  },

  growth: {
    assistant: 'Personal chats',
    assistantIntro:
      'Answer customers in your own Telegram chats, not just in the shop bot.',
    assistantSetup:
      'Open Telegram → Settings → Telegram Business → Chat Automation, then choose {{bot}}.',
    assistantConnected: 'Connected',
    assistantNotConnected: 'Not connected yet',
    assistantNoReply: 'Connected, but not allowed to reply. Grant the reply right in Telegram.',
    assistantMode: 'How it answers',
    assistantModeDraft: 'Suggest a reply to me first',
    assistantModeAuto: 'Reply to the customer directly',
    assistantTakeover:
      'Whenever you answer a chat yourself, the assistant goes quiet in that chat.',
    marketing: 'Marketing',
    analytics: 'Analytics',
    promotions: 'Promotions',
    newPromotion: 'New promotion',
    promoName: 'Campaign name',
    promoCodeOptional: 'Code (leave empty to apply automatically)',
    discountType: 'Discount',
    percentOff: 'Percent off',
    fixedOff: 'Fixed amount off',
    freeShipping: 'Free shipping',
    discountValue: 'Amount',
    maxDiscount: 'Cap the discount at',
    minSubtotal: 'Minimum order',
    startsAt: 'Starts',
    endsAt: 'Ends',
    customerSegment: 'Who it applies to',
    segmentAll: 'Everyone',
    segmentNew: 'First-time customers',
    segmentReturning: 'Returning customers',
    usageLimitTotal: 'Total uses',
    usageLimitPerCustomer: 'Uses per customer',
    priority: 'Priority',
    active: 'Active',
    paused: 'Paused',
    redemptions: '{{count}} used',
    discountGranted: 'Discount given',
    copyShareLink: 'Copy share link',
    deactivate: 'Deactivate',
    noPromotions: 'No promotions yet',
    noPromotionsHint:
      'A code you can paste into a Facebook post is the cheapest campaign you can run.',
    stackingNote:
      'Only one discount applies per order. A code the customer types beats an automatic promotion.',

    affiliates: 'Affiliates',
    newAffiliate: 'Add affiliate',
    affiliateName: 'Name',
    affiliateCode: 'Referral code',
    commissionRate: 'Commission',
    minPayout: 'Minimum payout',
    referralLink: 'Referral link',
    commissions: 'Commissions',
    commissionPending: 'Pending',
    commissionApproved: 'Ready to pay',
    commissionPaid: 'Paid',
    payOut: 'Record payout',
    payoutReference: 'Transfer reference',
    noAffiliates: 'No affiliates yet',
    noAffiliatesHint:
      'Give a reseller a link and they earn a cut of every order it brings in.',
    returnWindowNote:
      'A commission becomes payable {{days}} days after the order completes, once returns are no longer possible.',

    broadcasts: 'Broadcasts',
    newBroadcast: 'New broadcast',
    broadcastName: 'Internal name',
    broadcastBody: 'Message',
    broadcastButton: 'Button label',
    broadcastButtonUrl: 'Button link',
    audience: 'Audience',
    segmentWithOrders: 'Customers who have ordered',
    segmentNoOrders: 'Customers who never ordered',
    segmentRecent: 'Ordered in the last 60 days',
    segmentDormant: 'Quiet for over 60 days',
    recipients: '{{count}} customers',
    sendNow: 'Send now',
    sending: 'Sending...',
    sentSummary: '{{sent}} sent - {{blocked}} blocked - {{failed}} failed',
    cancelBroadcast: 'Stop',
    noBroadcasts: 'No broadcasts yet',
    pacedNote:
      'Messages go out gradually so your bot keeps answering customers while the broadcast runs.',

    automation: 'Automatic messages',
    paymentReminder: 'Remind customers before their payment code expires',
    reminderMinutes: 'Remind this many minutes before it expires',
    winBack: 'Message customers who have gone quiet',
    winBackDays: 'After this many days without an order',
    quietHours: 'Quiet hours',
    quietHoursHint: 'No automatic message is sent between these times.',

    range7: 'Last 7 days',
    range30: 'Last 30 days',
    range90: 'Last 90 days',
    grossRevenue: 'Revenue',
    averageOrder: 'Average order',
    discountsGiven: 'Discounts given',
    codOutstanding: 'COD not yet remitted',
    codShipped: 'Shipped COD',
    codCollected: 'Collected by courier',
    codRemitted: 'Remitted to you',
    topProducts: 'Best sellers',
    topVariants: 'Best size / colour',
    acquisition: 'Where orders came from',
    units: '{{count}} sold',
    noData: 'No orders in this period',
  },

  errors: {
    unauthenticated: 'Please sign in again',
    forbidden: 'You do not have access to this',
    shop_not_found: 'Shop not found',
    shop_suspended: 'This shop is suspended',
    not_a_member: 'You are not a member of this shop',
    validation_failed: 'Please check the highlighted fields',
    not_found: 'Not found',
    conflict: 'That conflicts with something that already exists',
    slug_taken: 'That shop address is already taken',
    product_unavailable: 'This product is no longer available',
    variant_unavailable: 'That option is no longer available',
    out_of_stock: 'Not enough stock left',
    invalid_transition: 'That action is not possible for this order right now',
    order_locked: 'This order can no longer be changed',
    empty_cart: 'Your cart is empty',
    currency_mismatch: 'Currency mismatch',
    shipping_unavailable: 'The shop does not deliver to that province',
    payment_provider_unavailable: 'Payments are temporarily unavailable — please try again shortly',
    payment_already_confirmed: 'This payment is already confirmed',
    payment_expired: 'This payment code has expired',
    unique_amount_exhausted: 'Too many orders are pending at this amount — please try again shortly',
    khqr_config_missing: 'The shop has not finished payment setup',
    plan_required: 'This feature needs a higher plan',
    policy_not_accepted: 'Accept the platform policies before you publish',
    insufficient_credits: 'Not enough credits',
    quota_exceeded: 'You have hit this month’s limit',
    rate_limited: 'Too many requests — please slow down',
    upstream_failed: 'A service we depend on is not responding',
    internal: 'Something went wrong on our side',
  },

  /** §8's onboarding checklist. */
  setup: {
    title: 'Finish setting up',
    intro: 'A few things left before your shop can take its first order.',
    acceptPolicies: 'Accept the platform policies',
    acceptPoliciesHint: 'Required before you can publish anything for sale.',
    addProduct: 'Publish your first product',
    addProductHint: 'Add one by hand, or import a Facebook post.',
    chooseTheme: 'Pick how your shop looks',
    chooseThemeHint: 'A template, your colour, your logo.',
    connectBot: 'Connect your Telegram bot',
    connectBotHint: 'Customers get order updates in Telegram. Your shop works without it.',
    paymentDetails: 'Set up how you get paid',
    paymentDetailsHint: 'Your ABA account for KHQR, or cash on delivery only.',
    viewStorefront: 'See your shop the way customers do',
  },


};

/**
 * Structural type for a dictionary: the same key tree, with plain `string`
 * leaves. `km.ts` is typed against it, so a missing or misspelled Khmer key is
 * a compile error, while the actual Khmer text is free to differ.
 */
export type Dictionary = typeof en;
